/**
 * 受限 Markdown 编辑器（2026-09-27 公告正文工具栏，待办清单 §4 第 3 项）。
 * EasyMDE 封装——工具栏只留渲染白名单内的语法（加粗/斜体/链接/对齐/撤销重做/预览），
 * 对齐用 `::: center|right … :::` 围栏段（与 restricted-md.util/mdToHtml 同语法，
 * 服务端真渲染——编辑器能写的每个语法前台都生效）。
 * 懒加载：EasyMDE ≈300KB（gzip≈80KB）动态 import 进编辑页不进 admin 主包。
 * 样式协调：import easymde CSS + admin token 覆写（gold 焦点态）。
 */
import { useEffect, useRef, useState } from 'react';
import type EasyMDE from 'easymde';

interface RestrictedMdEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** 双语 placeholder（对齐语法示例含在内） */
  placeholder: string;
  /** 无障碍名（多实例区分） */
  ariaLabel: string;
}

/** 工具栏定义（EasyMDE CustomToolbarOption 形态；白名单外一概不放） */
const TOOLBAR: (string | object)[] = [
  'bold',
  'italic',
  'link',
  '|',
  {
    name: 'align-center',
    action: (editor: EasyMDE) => wrapSelectionWithFence(editor, 'center'),
    className: 'fa fa-align-center',
    title: '居中段落（::: center 围栏）',
  },
  {
    name: 'align-right',
    action: (editor: EasyMDE) => wrapSelectionWithFence(editor, 'right'),
    className: 'fa fa-align-right',
    title: '右对齐段落（::: right 围栏，落款用）',
  },
  '|',
  'undo',
  'redo',
  '|',
  'preview',
];

/**
 * 把选区包进对齐围栏（选区生效——用户核心要求）。
 * 已在围栏内的选区直接整段替换时保持简单语义：外包新围栏（EasyMDE 无解析）。
 * 选区为空时插入空围栏占位（光标落中间由 codemirror cursorChosen 处理，此处用
 * setSelection 兜底）。
 */
function wrapSelectionWithFence(editor: EasyMDE, align: 'center' | 'right'): void {
  // 选区操作走 codemirror 实例（EasyMDE 对象无 getSelection——类型实测）
  const cm = editor.codemirror;
  const selection = cm.getSelection() ?? '';
  const tagOpen = `::: ${align}\n`;
  const tagClose = '\n:::';
  cm.replaceSelection(`${tagOpen}${selection}${tagClose}`);
  // 选区为空时把光标挪进围栏内（替换后光标在插入串末尾，倒退闭标记长度）
  if (!selection) {
    const doc = cm.getDoc();
    const end = doc.indexFromPos(doc.getCursor());
    doc.setCursor(doc.posFromIndex(Math.max(0, end - tagClose.length)));
  }
  cm.focus();
}

/** EasyMDE 懒加载封装：挂载后动态 import 初始化，卸载清理 */
export function RestrictedMdEditor({ value, onChange, placeholder, ariaLabel }: RestrictedMdEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EasyMDE | null>(null);
  const [ready, setReady] = useState(false);
  // onChange 引用最新（编辑器回调闭包外置）
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // 外部值同步守卫：仅当外部值与编辑器当前值不同（如表单重置）才回写，避免光标跳动
  const externalValueRef = useRef(value);

  useEffect(() => {
    let cancelled = false;
    let instance: EasyMDE | null = null;
    void (async () => {
      const [{ default: EasyMDECtor }] = await Promise.all([
        import('easymde'),
        import('easymde/dist/easymde.min.css'),
      ]);
      if (cancelled || !containerRef.current) return;
      const textarea = document.createElement('textarea');
      containerRef.current.appendChild(textarea);
      instance = new EasyMDECtor({
        element: textarea,
        initialValue: value,
        placeholder,
        toolbar: TOOLBAR as never,
        // 白名单外语法一律不给入口；预览用内置渲染（仅编辑期预览，前台渲染以服务端为准）
        previewRender: (plainText) => {
          // 编辑器内置预览走简化渲染（粗略）：正式渲染以服务端 mdToHtml+sanitize 为准。
          // 这里只做 markdown-it 默认渲染的近似——不引额外依赖，直接转义展示原文+提示。
          const esc = (t: string) => t.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] ?? c);
          return plainText
            .split(/\n{2,}/)
            .map((p) => `<p style="margin:0 0 8px">${esc(p).replace(/\n/g, '<br>')}</p>`)
            .join('');
        },
        status: false,
        spellChecker: false,
        // admin 深色 token 协调（样式覆写见 global.css .restricted-md）
      });
      instance.codemirror.on('change', () => {
        onChangeRef.current(instance!.value());
      });
      editorRef.current = instance;
      setReady(true);
    })();
    return () => {
      cancelled = true;
      instance?.cleanup?.();
      instance?.toTextArea?.();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 外部值变化（表单加载/重置/机翻填充）→ 回写编辑器
  useEffect(() => {
    const ed = editorRef.current;
    if (ed && value !== externalValueRef.current && value !== ed.value()) {
      ed.value(value);
    }
    externalValueRef.current = value;
  }, [value]);

  return (
    <div
      className='restricted-md'
      ref={containerRef}
      aria-label={ariaLabel}
      data-ready={ready ? '1' : '0'}
    />
  );
}
